import React, { useState, useEffect, useCallback } from 'react'
import {
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  Server,
  RefreshCw,
  AlertCircle
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import StatusBadge from '../../components/admin/StatusBadge'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import Toast, { ToastContainer } from '../../components/admin/Toast'
import ProviderForm from './components/ProviderForm'
import AccountsList from './components/AccountsList'

// Provider icons/colors mapping
const providerConfig = {
  openai: { color: 'bg-green-500', label: 'OpenAI' },
  anthropic: { color: 'bg-orange-500', label: 'Anthropic' },
  gemini: { color: 'bg-blue-500', label: 'Google Gemini' },
  grok: { color: 'bg-purple-500', label: 'Grok (xAI)' },
  deepseek: { color: 'bg-cyan-500', label: 'DeepSeek' },
}

const ProvidersPage = () => {
  // State
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedConfig, setExpandedConfig] = useState(null)

  // Modal states
  const [showProviderForm, setShowProviderForm] = useState(false)
  const [editingProvider, setEditingProvider] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, provider: null })
  const [isDeleting, setIsDeleting] = useState(false)

  // Toast state
  const [toasts, setToasts] = useState([])

  // Add toast helper
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Fetch configs
  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminApi.getConfigs()
      setConfigs(data)
    } catch (err) {
      console.error('Failed to fetch configs:', err)
      setError(err.response?.data?.detail || 'Failed to load providers')
      addToast('Failed to load providers', 'error')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    fetchConfigs()
  }, [fetchConfigs])

  // Toggle expanded row
  const toggleExpand = (configId) => {
    setExpandedConfig(expandedConfig === configId ? null : configId)
  }

  // Open create provider form
  const handleAddProvider = () => {
    setEditingProvider(null)
    setShowProviderForm(true)
  }

  // Open edit provider form
  const handleEditProvider = (provider) => {
    setEditingProvider(provider)
    setShowProviderForm(true)
  }

  // Handle provider form submit
  const handleProviderSubmit = async (data) => {
    try {
      if (editingProvider) {
        await adminApi.updateConfig(editingProvider.id, data)
        addToast('Provider updated successfully', 'success')
      } else {
        await adminApi.createConfig(data)
        addToast('Provider created successfully', 'success')
      }
      setShowProviderForm(false)
      setEditingProvider(null)
      fetchConfigs()
    } catch (err) {
      console.error('Failed to save provider:', err)
      addToast(err.response?.data?.detail || 'Failed to save provider', 'error')
      throw err
    }
  }

  // Confirm delete provider
  const handleDeleteClick = (provider) => {
    setDeleteConfirm({ open: true, provider })
  }

  // Execute delete provider
  const handleDeleteConfirm = async () => {
    const provider = deleteConfirm.provider
    if (!provider) return

    try {
      setIsDeleting(true)
      await adminApi.deleteConfig(provider.id)
      addToast('Provider deleted successfully', 'success')
      setDeleteConfirm({ open: false, provider: null })
      if (expandedConfig === provider.id) {
        setExpandedConfig(null)
      }
      fetchConfigs()
    } catch (err) {
      console.error('Failed to delete provider:', err)
      addToast(err.response?.data?.detail || 'Failed to delete provider', 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle account changes (refresh parent)
  const handleAccountsChange = () => {
    fetchConfigs()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            LLM Providers
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage translation provider configurations and model accounts
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchConfigs}
            disabled={loading}
            className={clsx(
              'inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600',
              'rounded-md shadow-sm text-sm font-medium',
              'text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700',
              'hover:bg-gray-50 dark:hover:bg-gray-600',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={handleAddProvider}
            data-testid="add-provider-button"
            className={clsx(
              'inline-flex items-center px-4 py-2',
              'border border-transparent rounded-md shadow-sm text-sm font-medium',
              'text-white bg-primary-600 hover:bg-primary-700',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            )}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Provider
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Providers table */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden" data-testid="providers-table-container">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="providers-table">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="w-10 px-4 py-3"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Provider
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Accounts
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Rate Limit
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading && configs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center">
                  <RefreshCw className="w-6 h-6 mx-auto text-gray-400 animate-spin" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Loading providers...
                  </p>
                </td>
              </tr>
            ) : configs.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center">
                  <Server className="w-8 h-8 mx-auto text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    No providers configured
                  </p>
                  <button
                    onClick={handleAddProvider}
                    className="mt-3 text-sm text-primary-600 hover:text-primary-500"
                  >
                    Add your first provider
                  </button>
                </td>
              </tr>
            ) : (
              configs.map((config) => {
                const isExpanded = expandedConfig === config.id
                const providerInfo = providerConfig[config.provider] || {
                  color: 'bg-gray-500',
                  label: config.provider
                }
                const accountCount = config.accounts?.length || 0
                const activeAccountCount = config.accounts?.filter(a => a.is_active).length || 0

                return (
                  <React.Fragment key={config.id}>
                    {/* Provider row */}
                    <tr
                      className={clsx(
                        'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer',
                        isExpanded && 'bg-gray-50 dark:bg-gray-700/50'
                      )}
                      onClick={() => toggleExpand(config.id)}
                      data-testid="provider-row"
                      data-provider-id={config.id}
                      data-provider-name={config.name}
                    >
                      <td className="px-4 py-4">
                        <button className="text-gray-400">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <span className={clsx('w-2 h-2 rounded-full mr-2', providerInfo.color)} />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {providerInfo.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {config.name}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {activeAccountCount}/{accountCount} active
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge status={config.is_active ? 'active' : 'inactive'} />
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {config.max_rps || '-'} RPS
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => handleEditProvider(config)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="Edit provider"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(config)}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title="Delete provider"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded accounts section */}
                    {isExpanded && (
                      <tr>
                        <td colSpan="7" className="px-4 py-4 bg-gray-50 dark:bg-gray-900/30">
                          <AccountsList
                            configId={config.id}
                            accounts={config.accounts || []}
                            onAccountsChange={handleAccountsChange}
                            addToast={addToast}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Provider Form Modal */}
      {showProviderForm && (
        <ProviderForm
          provider={editingProvider}
          onSubmit={handleProviderSubmit}
          onClose={() => {
            setShowProviderForm(false)
            setEditingProvider(null)
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, provider: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Provider"
        message={`Are you sure you want to delete "${deleteConfirm.provider?.name}"? This will also delete all associated accounts. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Toasts */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  )
}

export default ProvidersPage
